import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
    DaysPicked,
    IProductsStatusHistory,
    ISchedule,
    ISchedulesReducer,
} from '../../types/schedule'
import { fetchProductJobSchedule } from '../async-thunks/schedules/fetchProductJobSchedule'
import { fetchProductSchedulesHistory } from '../async-thunks/schedules/fetchProductSchedulesHistory'
import _ from 'lodash'

const initialState: ISchedulesReducer = {
    jobSchedules: {
        isLoading: false,
        error: '',
        schedules: {},
        schedulesDefault: {},
    },
    schedulesHistory: {
        isLoading: false,
        error: '',
        productsStatusHistory: {},
    },
}

const schedulesSlice = createSlice({
    name: 'schedulesSlice',
    initialState,
    reducers: {
        refreshSchedulesCommonState: (state) => {
            return {
                ...state,
                jobSchedules: {
                    ...state.jobSchedules,
                    isLoading: false,
                    error: '',
                },
                schedulesHistory: {
                    ...state.schedulesHistory,
                    isLoading: false,
                    error: '',
                },
            }
        },
        addJobSchedule: (
            state,
            action: PayloadAction<{ guid: string; schedules: ISchedule[] }>
        ) => {
            const { guid, schedules } = action.payload
            state.jobSchedules.schedules[guid] = schedules
        },
        changeJobScheduleDateFrom: (
            state,
            action: PayloadAction<{
                guid: string
                index: number
                dateFrom: string
            }>
        ) => {
            const { guid, index, dateFrom } = action.payload
            state.jobSchedules.schedules[guid][index].period.from = dateFrom
        },
        changeJobScheduleDateTo: (
            state,
            action: PayloadAction<{
                guid: string
                index: number
                dateTo: string | null
            }>
        ) => {
            const { guid, index, dateTo } = action.payload
            state.jobSchedules.schedules[guid][index].period.to = dateTo
        },
        changeJobScheduleDays: (
            state,
            action: PayloadAction<{
                guid: string
                index: number
                days: DaysPicked
            }>
        ) => {
            const { guid, index, days } = action.payload
            state.jobSchedules.schedules[guid][index].days_of_week = days
        },

        changeJobScheduleTimes: (
            state,
            action: PayloadAction<{
                guid: string
                index: number
                times: Array<string | null>
            }>
        ) => {
            const { guid, index, times } = action.payload
            state.jobSchedules.schedules[guid][index].start_time = times
        },
        deleteJobSchedule: (
            state,
            action: PayloadAction<{
                guid: string
                updatedScheduleList: ISchedule[]
            }>
        ) => {
            const { guid, updatedScheduleList } = action.payload
            state.jobSchedules.schedules[guid] = updatedScheduleList
        },
        toggleJobScheduleState: (
            state,
            action: PayloadAction<{
                guid: string
                index: number
                isActive: boolean
            }>
        ) => {
            const { guid, index, isActive } = action.payload
            state.jobSchedules.schedules[guid][index].is_active = isActive
        },
        duplicateJobSchedule: (
            state,
            action: PayloadAction<{
                guid: string
                index: number
            }>
        ) => {
            const { guid, index } = action.payload
            const scheduleToDuplicate =
                state.jobSchedules.schedules[guid][index]
            const newSchedule = _.cloneDeep(scheduleToDuplicate)
            state.jobSchedules.schedules[guid].push(newSchedule)
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchProductJobSchedule.pending, (state) => {
                state.jobSchedules.isLoading = true
                state.jobSchedules.error = ''
            })
            .addCase(fetchProductJobSchedule.fulfilled, (state, action) => {
                state.jobSchedules.isLoading = false
                state.jobSchedules.error = ''
                state.jobSchedules.schedules[action.payload.guid] =
                    action.payload.data
                state.jobSchedules.schedulesDefault[action.payload.guid] =
                    action.payload.data
            })
            .addCase(fetchProductJobSchedule.rejected, (state) => {
                state.jobSchedules.isLoading = false
                state.jobSchedules.error = 'errors.messages.onScheduleFetch'
            })
            .addCase(fetchProductSchedulesHistory.pending, (state) => {
                state.schedulesHistory.isLoading = true
                state.schedulesHistory.error = ''
            })
            .addCase(
                fetchProductSchedulesHistory.fulfilled,
                (
                    state,
                    action: PayloadAction<{
                        guid: string
                        data: IProductsStatusHistory
                    } | void>
                ) => {
                    if (!action.payload) return
                    const { guid, data } = action.payload
                    state.schedulesHistory.isLoading = false
                    state.schedulesHistory.error = ''
                    state.schedulesHistory.productsStatusHistory[guid] = data
                }
            )
            .addCase(fetchProductSchedulesHistory.rejected, (state) => {
                state.schedulesHistory.isLoading = false
                state.schedulesHistory.error =
                    'errors.messages.onScheduleHistoryFetch'
            })
    },
})

export const { actions: schedulesActions } = schedulesSlice
export const { reducer: schedulesReducer } = schedulesSlice
